import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '@/hooks/useAuth';
import { functions, db, auth, logAnalyticsEvent } from '@/lib/firebase';
import { oaiqOrderCreated, setOaiqUserEmail } from '@/lib/oaiq';
import { metaPurchase, setMetaUserEmail } from '@/lib/metaPixel';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { GlassCard } from '@/components/ui/GlassCard';
import { CheckCircle } from 'lucide-react';
import { PLAN_CONFIGS, type PlanId } from '@shared/types';

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Three post-payment shapes arrive here:
  //   session_id  -> Stripe-hosted Checkout (the mobile funnel). Handles BOTH
  //                  subscriptions and lifetime, via linkStripeSession.
  //   cid + sid   -> embedded Payment Element, subscription.
  //   cid + pid   -> embedded Payment Element, one-time (lifetime).
  //
  // The pid branch did not exist. CheckoutPage has always sent lifetime buyers
  // here with ?pid=, but this page only looked for sid - so hasPaymentContext was
  // false, the effect below bounced them to /pricing, and linkGuestLifetime (which
  // exists on the server) had ZERO callers. A buyer on that path was charged $99
  // and got no account.
  const sessionId = searchParams.get('session_id');
  const customerId = searchParams.get('cid');
  const subscriptionId = searchParams.get('sid');
  const paymentIntentId = searchParams.get('pid');
  const prefilledEmail = searchParams.get('email');
  const planParam = searchParams.get('plan') as PlanId | null;
  const isEmbeddedFlow = !!customerId && (!!subscriptionId || !!paymentIntentId);
  const isOneTimeFlow = !!customerId && !!paymentIntentId;
  const hasPaymentContext = !!sessionId || isEmbeddedFlow;

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(prefilledEmail || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();

  // Register is only accessible after a successful payment. If no payment
  // context is present (legacy session_id OR embedded cid+sid), bounce to pricing.
  useEffect(() => {
    if (!hasPaymentContext) {
      navigate('/pricing');
    }
  }, [hasPaymentContext, navigate]);

  const waitForActivation = (userId: string): Promise<boolean> =>
    new Promise((resolve) => {
      const unsubscribe = onSnapshot(doc(db, 'users', userId), (docSnap) => {
        if (docSnap.exists() && docSnap.data()?.subscriptionActive === true) {
          unsubscribe();
          resolve(true);
        }
      });
      setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, 10000);
    });

  const firePurchaseEvent = async (userId: string): Promise<void> => {
    try {
      let resolvedPlanId: PlanId | null =
        planParam && PLAN_CONFIGS[planParam] ? planParam : null;
      if (!resolvedPlanId) {
        const userSnap = await new Promise<{ planId?: PlanId } | null>((resolve) => {
          const t = setTimeout(() => resolve(null), 2000);
          const unsub = onSnapshot(doc(db, 'users', userId), (snap) => {
            if (snap.exists()) {
              clearTimeout(t);
              unsub();
              resolve(snap.data() as { planId?: PlanId });
            }
          });
        });
        if (userSnap?.planId && PLAN_CONFIGS[userSnap.planId]) {
          resolvedPlanId = userSnap.planId;
        }
      }
      if (!resolvedPlanId) return;
      const planConfig = PLAN_CONFIGS[resolvedPlanId];
      // OpenAI/ChatGPT Ads conversion. This is the one point BOTH funnels pass
      // through: mobile arrives with ?session_id from Stripe-hosted checkout
      // (where no pixel of ours could run), desktop with cid+sid/pid. The
      // event_id is the Stripe object id, which is also what /checkout sends -
      // so the desktop buyer seen twice still counts once.
      //
      // Hash the email first: the mobile buyer just came back from a different
      // origin (checkout.stripe.com), which is exactly when cookie-only
      // attribution is weakest and advanced matching earns its keep.
      const matchEmail = auth.currentUser?.email || email;
      if (matchEmail) await Promise.all([setOaiqUserEmail(matchEmail), setMetaUserEmail(matchEmail)]);
      oaiqOrderCreated({
        eventId: subscriptionId || paymentIntentId || sessionId || userId,
        planId: resolvedPlanId,
        planName: planConfig.name,
        amountCents: Math.round(planConfig.price * 100),
      });
      metaPurchase({
        eventId: subscriptionId || paymentIntentId || sessionId || userId,
        planId: resolvedPlanId,
        planName: planConfig.name,
        amountCents: Math.round(planConfig.price * 100),
      });
      logAnalyticsEvent('purchase', {
        transaction_id: subscriptionId || sessionId || userId,
        value: planConfig.price,
        currency: 'USD',
        items: [
          {
            item_id: resolvedPlanId,
            item_name: planConfig.name,
            price: planConfig.price,
            quantity: 1,
          },
        ],
      });
    } catch {
      // Analytics must never block the success path.
    }
  };

  const linkPayment = async (userId: string): Promise<boolean> => {
    try {
      if (isOneTimeFlow) {
        const linkLifetime = httpsCallable<
          { customerId: string; paymentIntentId: string; fullName?: string },
          { success: boolean }
        >(functions, 'linkGuestLifetime');
        const resolvedName = (fullName?.trim() || auth.currentUser?.displayName || undefined);
        await linkLifetime({
          customerId: customerId!,
          paymentIntentId: paymentIntentId!,
          fullName: resolvedName,
        });
      } else if (isEmbeddedFlow) {
        const linkGuest = httpsCallable<
          { customerId: string; subscriptionId: string; fullName?: string },
          { success: boolean }
        >(functions, 'linkGuestSubscription');
        // Pull a sensible display name: typed full name on email signup,
        // or the Google account's displayName on social signup.
        const resolvedName = (fullName?.trim() || auth.currentUser?.displayName || undefined);
        await linkGuest({
          customerId: customerId!,
          subscriptionId: subscriptionId!,
          fullName: resolvedName,
        });
      } else if (sessionId) {
        const linkSession = httpsCallable<{ sessionId: string }, { success: boolean }>(
          functions,
          'linkStripeSession'
        );
        await linkSession({ sessionId });
      } else {
        return false;
      }
      return waitForActivation(userId);
    } catch (error) {
      console.error('Failed to link payment:', error);
      // Last-resort: the payment definitely happened (they came from Stripe), so
      // find it by email and finish the linkage server-side. Without this, one
      // failed call leaves someone who paid $99 permanently locked out - there is
      // no webhook fallback for a guest one-time purchase, and
      // syncSubscriptionStatus can never find a PaymentIntent.
      try {
        const recover = httpsCallable<Record<string, never>, { recovered: boolean }>(
          functions,
          'recoverPurchase',
        );
        const { data } = await recover({});
        if (data.recovered) return waitForActivation(userId);
      } catch {
        /* fall through to the support message */
      }
      toast.error('Account created but activation failed. Please contact support.');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, fullName);

      // Wait a moment for auth state to update
      await new Promise((resolve) => setTimeout(resolve, 500));

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Failed to get user after signup');
      }

      const success = await linkPayment(currentUser.uid);

      if (success) {
        await firePurchaseEvent(currentUser.uid);
        toast.success('Account created successfully!');
        navigate('/onboarding');
      } else {
        toast.error('Subscription activation timed out. Please try signing in.');
        navigate('/login');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create account';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();

      const currentUser = auth.currentUser;
      if (currentUser) {
        const success = await linkPayment(currentUser.uid);

        if (success) {
          await firePurchaseEvent(currentUser.uid);
          toast.success('Welcome to Romulus!');
          navigate('/onboarding');
        } else {
          toast.error('Subscription activation timed out. Please try signing in again.');
          navigate('/login');
        }
      }
    } catch (error) {
      toast.error('Failed to sign in with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Don't render while redirecting away on missing payment context
  if (!hasPaymentContext) {
    return null;
  }

  return (
    <GlassCard glow="purple">
      <div className="text-center mb-8">
        <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-6 h-6 text-primary-400" />
        </div>
        <h2 className="text-2xl font-bold text-snow font-sans">Payment Successful!</h2>
        <p className="text-gray-400 mt-2">Create your account to get started</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name</Label>
          <Input
            id="fullName"
            type="text"
            placeholder="John Doe"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <p className="text-xs text-gray-500">Must be at least 6 characters</p>
        </div>

        <Button type="submit" variant="accent" className="w-full" loading={loading}>
          Create Account
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-navy-900 px-2 text-gray-500">Or continue with</span>
        </div>
      </div>

      <Button variant="outline" className="w-full" onClick={handleGoogleSignIn} loading={googleLoading} disabled={googleLoading}>
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Continue with Google
      </Button>

      <p className="mt-6 text-center text-sm text-gray-400">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">
          Sign in
        </Link>
      </p>
    </GlassCard>
  );
}
